import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import { MenuItemPreviewSearchFragment } from "apps/web/src/types";
import { ReactNode, useState } from "react";
import { MenuItemsPanelDragOverlay } from "./MenuItemsPanelDragOverlay";
import { isDayCellDropData, isPanelMenuItemDragData, panelToCellCollisionDetection, toMenuBuilderMenuItem } from "./dndTypes";

interface MenuBuilderPanelToCellDndProps {
  children: ReactNode;
  enabled: boolean;
}

/**
 * Outer DnD context for library panel → day/station cell assignment.
 * Nested in-cell reorder keeps its own DndContext inside each cell.
 * When the flag is off this is a pass-through so cells do not pay for droppables.
 */
export const MenuBuilderPanelToCellDnd = ({ children, enabled }: MenuBuilderPanelToCellDndProps) => {
  if (!enabled) {
    return <>{children}</>;
  }

  return <MenuBuilderPanelToCellDndEnabled>{children}</MenuBuilderPanelToCellDndEnabled>;
};

const MenuBuilderPanelToCellDndEnabled = ({ children }: { children: ReactNode }) => {
  const { setMessage } = useSnackbar();
  const [activeMenuItem, setActiveMenuItem] = useState<MenuItemPreviewSearchFragment | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { data } = event.active;
    if (isPanelMenuItemDragData(data.current)) {
      setActiveMenuItem(data.current.menuItem);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveMenuItem(null);

    const { active, over } = event;
    if (!over || !isPanelMenuItemDragData(active.data.current) || !isDayCellDropData(over.data.current)) {
      return;
    }

    const { menuItem } = active.data.current;
    const { addRowItem, usedMenuItemIds } = over.data.current;

    if (usedMenuItemIds.includes(menuItem.id)) {
      setMessage("error", `Menu Item '${menuItem.name}' already exists on this menu`);
      return;
    }

    addRowItem(toMenuBuilderMenuItem(menuItem));
  };

  const handleDragCancel = () => {
    setActiveMenuItem(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={panelToCellCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {children}
      <DragOverlay>{activeMenuItem ? <MenuItemsPanelDragOverlay menuItem={activeMenuItem} /> : null}</DragOverlay>
    </DndContext>
  );
};
